import React from 'react'
import HubBadge from 'components/sidebar/hubBadge'
import { Docs, Projects, SearchLine } from 'components/icons'
import {
  activeRowStyle,
  SIDEBAR_HEADER_PAD,
  SIDEBAR_HERO_BADGE,
  SIDEBAR_ICON,
  SIDEBAR_ROW,
  SIDEBAR_ROW_PAD
} from 'components/sidebar/surface'

// The panel, and as much of the page beside it as says "this is the sidebar": a fifth of its width.
const SIDEBAR_WIDTH = 240
const PAGE_WIDTH = SIDEBAR_WIDTH / 5

// Fading out above and below the hub, and across the page beside the panel, so the hub's own rows are
// what's in focus.
const FADE = [
  'linear-gradient(to bottom, transparent, #000 30%, #000 80%, transparent)',
  `linear-gradient(to right, #000 ${SIDEBAR_WIDTH}px, transparent)`
].join(', ')

const ROW = `${SIDEBAR_ROW} ${SIDEBAR_ROW_PAD} cursor-default`

/** A placeholder line on the page beside the panel, very light, as a loading page's are. */
const Bar: React.FC<{ width: string }> = ({ width }) => (
  <span className="block h-1.5 rounded-full bg-divider/60" style={{ width }} />
)

/**
 * A sliver of the sidebar in the hub's theme, drawn with the sidebar's own rows: the top rows and the
 * rule under them, the hub's title with its icon, and the page open in it highlighted in its colour,
 * with the start of the page beside the panel as a light skeleton. Everything round the hub fades out.
 * Square-cornered, as the panel is. The Hub theme card's preview, above its colours.
 */
const HubPreview: React.FC<{ name: string; colour: string; textColour: string }> = ({ name, colour, textColour }) => (
  <div
    aria-hidden
    data-testid="hub-colours-preview"
    className="flex"
    style={{ maskImage: FADE, WebkitMaskImage: FADE, maskComposite: 'intersect', WebkitMaskComposite: 'source-in' }}
  >
    <div className="flex flex-col border-r border-sidebar-line bg-sidebar p-1.5" style={{ width: SIDEBAR_WIDTH }}>
      <div className={`${ROW} text-body`}>
        <span className={SIDEBAR_ICON}>
          <SearchLine />
        </span>
        Search
      </div>
      <div className={`${ROW} text-body`}>
        <span className={SIDEBAR_ICON}>
          <Projects />
        </span>
        All hubs
      </div>
      {/* The rule between the rows that act on everything and the hub's contents. */}
      <div className="mx-1.5 mt-2 mb-1 border-t border-sidebar-line" />
      {/* The hub's title, at the sidebar's size for it: a bigger icon and name than a row's. */}
      <div className={`flex h-10 items-center ${SIDEBAR_HEADER_PAD}`}>
        <HubBadge name={name} colour={colour} textColour={textColour} size={SIDEBAR_HERO_BADGE} />
        <span className="ml-2 min-w-0 truncate text-[15px] font-semibold tracking-[-0.015em] text-body">{name}</span>
      </div>
      <div className={ROW} style={activeRowStyle(colour)}>
        <span className={SIDEBAR_ICON}>
          <Docs />
        </span>
        Project brief
      </div>
      <div className={`${ROW} text-body`}>
        <span className={SIDEBAR_ICON}>
          <Docs />
        </span>
        Site register
      </div>
    </div>
    {/* The start of the page: its header's rule, and a few lines of content. */}
    <div className="flex flex-col bg-surface" style={{ width: PAGE_WIDTH }}>
      <div className="flex h-10 items-center border-b border-divider px-2.5">
        <Bar width="70%" />
      </div>
      <div className="flex flex-col gap-2.5 px-2.5 pt-3">
        <Bar width="90%" />
        <Bar width="60%" />
        <Bar width="80%" />
      </div>
    </div>
  </div>
)

export default HubPreview
